import { ReactNode, useState } from 'react';
import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import { BodySmall } from 'styles/typography';
import CircleCheckIcon from 'svg/CircleCheckIcon';
import WarnIcon from 'svg/WarnIcon';

import {
  BENEFIT_WIDTHS,
  LinkButton,
  SectionCard,
  SectionCardHeader,
  SectionCardHint,
  SectionCardTitle,
  StcTable,
  Table,
  TableScroll,
  Td,
  Th,
} from './BenefitsTable';
import { BenefitsView, Cell, NOT_RETURNED, TableRow } from './newFormatBenefits';

// The plain-language benefits view, led by skilled nursing. The header row (payer, member ID, group
// number, DOB, status) is the slideout's details bar, so it isn't repeated here.

interface Props {
  view: BenefitsView;
  pdfAvailable: boolean;
}

export default function NewFormatBenefitsView({ view, pdfAvailable }: Props) {
  // Traditional Medicare sends no plan names, so a column of "Not returned by payer" would say nothing.
  const showPlan = view.coverage.some((row) => row.plan);
  const coverageColumns = showPlan
    ? ['Plan or program', 'Type', 'Status', 'Plan Dates']
    : ['Type', 'Status', 'Plan Dates'];
  // Without the plan column, Type is what names the row.
  const TypeCell = showPlan ? Td : Label;

  return (
    <Stack data-testid='new-format-benefits'>
      {!pdfAvailable && (
        <Note data-testid='eligibility-pdf-unavailable'>
          PDF download is unavailable for this benefit verification check
        </Note>
      )}

      <Section title='Coverage' testId='nf-coverage'>
        <SummaryTable columns={coverageColumns} widths={showPlan ? [29, 29, 18, 24] : [40, 25, 35]}>
          {view.coverage.length === 0 && (
            <tr>
              <Td colSpan={coverageColumns.length}>
                <Muted>{NOT_RETURNED}</Muted>
              </Td>
            </tr>
          )}
          {view.coverage.map((row, i) => (
            <tr key={i}>
              {showPlan && <Label>{row.plan ?? <Muted>{NOT_RETURNED}</Muted>}</Label>}
              <TypeCell>
                {row.type}
                {row.typeSub && <Sub>{row.typeSub}</Sub>}
              </TypeCell>
              <Td>{row.status}</Td>
              <Td>
                {row.dates}
                {row.datesSub && <Sub>{row.datesSub}</Sub>}
              </Td>
            </tr>
          ))}
        </SummaryTable>
      </Section>

      <Section title='Skilled Nursing' hint={view.snfHint} testId='nf-skilled-nursing'>
        {view.skilledNursing ? (
          <BenefitTable columns={view.skilledNursing.columns} rows={view.skilledNursing.rows} />
        ) : (
          <Empty>{view.snfEmpty}</Empty>
        )}
      </Section>

      <Section title='Plan-Level Costs' hint='STC 30 · Health Benefit Plan Coverage' testId='nf-plan-costs'>
        <BenefitTable columns={view.planCosts.columns} rows={view.planCosts.rows} />
      </Section>

      <Section title='Contacts' testId='nf-contacts'>
        {view.contacts.length === 0 ? (
          <Empty>No contacts were reported by this payer.</Empty>
        ) : (
          <Cards $columns={3}>
            {view.contacts.map((contact, i) => (
              <Card key={i}>
                <Sub>{contact.role}</Sub>
                <BodySmall>{contact.name}</BodySmall>
                <BodySmall>{contact.phone}</BodySmall>
              </Card>
            ))}
          </Cards>
        )}
      </Section>

      <Section title='Other Coverage and Patient Responsibility' testId='nf-other'>
        <Cards $columns={2}>
          {view.other.map((item) => (
            <Card key={item.label}>
              <Sub>{item.label}</Sub>
              {item.value ? <BodySmall>{item.value}</BodySmall> : <Muted>{NOT_RETURNED}</Muted>}
              {item.sub && <Sub>{item.sub}</Sub>}
            </Card>
          ))}
        </Cards>
      </Section>

      <Section title='Payer Notes' hint='Skilled nursing and plan-level messages' testId='nf-notes'>
        {view.notes.length === 0 ? (
          <Empty>No notes were reported by this payer.</Empty>
        ) : (
          view.notes.map((note, i) => (
            <PayerNote key={i}>
              {note.text}
              <Sub>{note.sub}</Sub>
            </PayerNote>
          ))
        )}
      </Section>

      <AllBenefits view={view} />

      <Note data-testid='nf-footer'>{view.footer}</Note>
    </Stack>
  );
}

function AllBenefits({ view }: { view: BenefitsView }) {
  const [open, setOpen] = useState(false);

  return (
    <Section title={`Benefits${view.planNumber ? ` – ${view.planNumber}` : ''}`} testId='nf-all-benefits' flush>
      {open &&
        view.allBenefits.map((group) => (
          <StcTable key={group.title} title={group.title} rows={group.rows} widths={BENEFIT_WIDTHS} />
        ))}
      <ToggleRow>
        <LinkButton type='button' onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid='nf-toggle-all'>
          {open
            ? 'Hide all benefits'
            : `Show all benefits (${view.allBenefits.length} service type${view.allBenefits.length === 1 ? '' : 's'})`}
        </LinkButton>
      </ToggleRow>
    </Section>
  );
}

// Column widths in percent, so the benefit column lines up across Skilled Nursing and Plan-Level Costs.
function SummaryTable({ columns, widths, children }: { columns: string[]; widths: number[]; children: ReactNode }) {
  return (
    <TableScroll>
      <Table>
        <colgroup>
          {widths.map((width, i) => (
            <col key={i} style={{ width: `${width}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((column) => (
              <Th key={column}>{column}</Th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </Table>
    </TableScroll>
  );
}

function BenefitTable({ columns, rows }: { columns: string[]; rows: TableRow[] }) {
  const widths = columns.length > 1 ? [26, 37, 37] : [26, 74];
  return (
    <SummaryTable columns={['Benefit', ...columns]} widths={widths}>
      {rows.map((row) => (
        <tr key={row.label} data-testid={`nf-row-${row.label}`}>
          <Label>{row.label}</Label>
          {row.cells.map((cell, i) => (
            <Td key={i}>
              <CellValue cell={cell} />
            </Td>
          ))}
        </tr>
      ))}
    </SummaryTable>
  );
}

function CellValue({ cell }: { cell: Cell }) {
  const lines = cell.lines.map((line, i) => <Line key={i}>{line}</Line>);
  let body: ReactNode = lines;
  if (cell.tone === 'ok') {
    body = (
      <Flagged $color={colors.greenExtraDark}>
        <CircleCheckIcon width={12} height={12} fill={colors.green100} aria-hidden='true' />
        {lines}
      </Flagged>
    );
  } else if (cell.tone === 'required') {
    body = (
      <Flagged $color={colors.yellowExtraDark}>
        <WarnIcon width={12} height={12} color={colors.yellow100} aria-hidden='true' />
        {lines}
      </Flagged>
    );
  } else if (cell.tone === 'muted') {
    body = <Muted>{lines}</Muted>;
  }
  return (
    <>
      {body}
      {cell.sub && <Sub>{cell.sub}</Sub>}
    </>
  );
}

function Section({
  title,
  hint,
  testId,
  flush = false,
  children,
}: {
  title: string;
  hint?: string;
  testId: string;
  // For a body that brings its own padding (the per-service-type tables under Show all benefits).
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <SectionCard data-testid={testId}>
      <SectionCardHeader>
        <SectionCardTitle as='h3'>{title}</SectionCardTitle>
        {hint && <SectionCardHint>{hint}</SectionCardHint>}
      </SectionCardHeader>
      {flush ? children : <SectionBody>{children}</SectionBody>}
    </SectionCard>
  );
}

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const SectionBody = styled.div`
  padding: 8px 24px 12px;
`;

const Cards = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(0, 1fr));
  gap: 16px 24px;
  padding: 8px 0;
`;

const ToggleRow = styled.div`
  padding: 16px 24px;
`;

// The first column of a benefits table names the row.
const Label = styled(Td).attrs({ as: 'td' })`
  font-weight: ${FONT_WEIGHT_MEDIUM};
`;

const Line = styled.span`
  display: block;

  & + & {
    margin-top: 4px;
  }
`;

const Flagged = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  font-weight: ${FONT_WEIGHT_MEDIUM};
  color: ${({ $color }) => $color};

  svg {
    flex-shrink: 0;
    margin-top: 4px;
  }
`;

// The app font has no italic face and the app turns off synthetic italics (index.css), so a muted
// value asks for them back to read as the design's italic "Not returned by payer".
const Muted = styled(BodySmall).attrs({ as: 'span' })`
  display: block;
  font-style: italic;
  font-synthesis: style;
  color: ${colors.black75};
`;

const Sub = styled(BodySmall).attrs({ as: 'span' })`
  display: block;
  font-size: ${({ theme }) => theme.textStyles.label.fontSize};
  line-height: 18px;
  font-weight: 400;
  color: ${colors.black75};
`;

const Note = styled(Sub).attrs({ as: 'p' })`
  margin: 0;
`;

const Empty = styled(BodySmall)`
  margin: 8px 0;
  color: ${colors.black75};
`;

const PayerNote = styled(BodySmall).attrs({ as: 'div' })`
  padding: 10px 0;

  & + & {
    border-top: 1px solid ${colors.black10};
  }
`;
